/*
 * Passport / document photo — workspace UI.
 *
 * Pick a document → read its spec sheet → upload → the face is found and fitted
 * → adjust, replace the background, add the corner → check → download or print.
 *
 * Three things here exist because production said they had to (2026-09-08):
 *
 *  1. THE DOCUMENT IS CHOSEN, NOT INHERITED. It used to be `Object.keys(SPECS)[0]`
 *     — "US Passport" — on a domain that serves one language, Russian. See
 *     app/Support/PassportPhotoSpecs.php for the numbers; the short version is
 *     that 16.8% of exports were a 51×51 mm square nobody asked for. The picked
 *     document is now locale-aware, remembered between visits, and printed on
 *     the download button, so getting it wrong takes effort.
 *  2. "NO FACE" IS A QUESTION, NOT A VERDICT. 14.8% of uploads ended there. The
 *     ladder in the engine answers most of them; when it still cannot, this
 *     layer asks the person to point at the face instead of shrugging.
 *  3. THE TELEMETRY IS REAL. This file used to call `Timbrica3D.init(slug)`,
 *     which is the 3D-printing core: on any other tool it returns null and
 *     every `tel.feature(...)` after it is a no-op. Every "which document did
 *     they pick / did they use the background / did they print a sheet"
 *     question was unanswerable, and nothing said so.
 *
 * Depends on: PassportPhotoEngine, PassportPrintSheet, FRLandmarks/FRFind,
 * BgMatting, window.__PP_{SPECS,GROUPS,FIRST,T,SOURCES,MATTING}.
 */
(function () {
    'use strict';

    var E = null;
    var SPECS = window.__PP_SPECS || {};
    var GROUPS = (window.__PP_GROUPS || []).slice();
    var T = window.__PP_T || { ui: {}, cl: {}, docs: {}, groups: {} };
    var MAX_DISP = 520, MAX_PREV = 300, HIT = 12;
    var CUSTOM = 'custom';

    /* Own numbers, as a spec record like any other. Keeping it in SPECS rather
       than as a special case is what makes every downstream step — transform,
       checklist, corner, print sheet — work on it without a single branch. */
    var CUSTOM_DEFAULT = { w: 35, h: 45, headMin: 32, headMax: 36, top: 5, bg: '#ffffff' };

    function buildCustomSpec(v) {
        var w = clampNum(v.w, 15, 150, 35), h = clampNum(v.h, 15, 150, 45);
        var hmin = clampNum(v.headMin, 5, h, Math.round(h * 0.7));
        var hmax = clampNum(v.headMax, hmin, h, Math.min(h, hmin + 4));
        var top = clampNum(v.top, 0, Math.max(0, h - hmax), 5);
        var dpi = 300;
        return {
            label_en: 'Custom', label_ru: 'Свой размер',
            country: CUSTOM, doc_type: 'generic',
            print_w_mm: w, print_h_mm: h, dpi: dpi,
            target_px_w: Math.round(w / 25.4 * dpi), target_px_h: Math.round(h / 25.4 * dpi),
            px_mode: 'min',
            head_mode: 'ru_inverted', head_mm_min: hmin, head_mm_max: hmax, top_margin_mm: top,
            head_pct_min: Math.round(hmin / h * 100), head_pct_max: Math.round(hmax / h * 100),
            bg: 'custom', bg_hex: /^#[0-9a-f]{6}$/i.test(v.bg || '') ? v.bg : '#ffffff',
            color_mode: 'color', filesize_min_kb: null, filesize_max_kb: null,
            allow_ai_retouch: true, glasses_allowed: true,
            corner: { on: false, mm: 10, side: 'right' },
            confidence: 'medium', verify_note: false, source_url: null,
        };
    }
    function clampNum(v, lo, hi, dflt) {
        var n = Math.round(+v);
        if (!isFinite(n)) return dflt;
        return Math.max(lo, Math.min(hi, n));
    }
    function customValues() {
        return {
            w: +($('ppCW') || {}).value, h: +($('ppCH') || {}).value,
            headMin: +($('ppCHmin') || {}).value, headMax: +($('ppCHmax') || {}).value,
            top: +($('ppCTop') || {}).value, bg: ($('ppCBg') || {}).value
        };
    }

    var st = {
        src: null, geom: null, k: 0.12, manualChinY: null, manualCrownY: null,
        deTilt: true, bgMode: 'none', gray: false,
        corner: { on: false, mm: 10, side: 'right', outline: false },
        specId: window.__PP_FIRST || Object.keys(SPECS)[0] || null,
        panX: 0, panY: 0, zoom: 1, rotateExtra: 0,
        dispScale: 1, tr: null, _g: null, _crownY: 0, _base: null, _bgApplied: null, _rendered: null, _a: null,
        drag: null, _panStart: null, _pointMode: false, _checklist: null
    };

    function $(id) { return document.getElementById(id); }
    function u(k, d) { return (T.ui && T.ui[k]) || d || ''; }
    function cl(k, d) { return (T.cl && T.cl[k]) || d || k; }
    function ae(type, data) { try { if (window.__ae) window.__ae(type, data || {}); } catch (_) { } }
    function feat(f, data) { ae('feature_use', Object.assign({ slug: 'passport-photo', f: f }, data || {})); }
    function stage(s, meta) { try { if (window.__stage) window.__stage(s, meta || {}); } catch (_) { } }

    function docLabel(id) {
        if (id === CUSTOM) return u('custom_title', 'Custom size');
        var s = SPECS[id]; if (!s) return id;
        if (T.docs && T.docs[id]) return T.docs[id];
        return (window.__LOCALE === 'ru' && s.label_ru) ? s.label_ru : s.label_en;
    }
    function sizeLabel(spec) { return spec.print_w_mm + ' × ' + spec.print_h_mm + ' ' + u('mm', 'mm'); }
    function headMmRange(spec) {
        if (spec.head_mode === 'ru_inverted' && spec.head_mm_min) return spec.head_mm_min + '–' + spec.head_mm_max + ' ' + u('mm', 'mm');
        return spec.head_pct_min + '–' + spec.head_pct_max + '%';
    }
    function specCorner(spec) { return spec && spec.corner ? spec.corner : null; }

    // ── busy overlay ────────────────────────────────────────────────────────
    function busy(text, sub, pct) {
        var b = $('ppBusy');
        if ($('ppBusyText')) $('ppBusyText').textContent = text || '';
        if ($('ppBusySub')) $('ppBusySub').textContent = sub || '';
        var bar = $('ppBusyBar');
        if (bar) bar.style.width = (pct == null ? '' : Math.max(2, Math.min(100, pct)) + '%');
        if (b) b.hidden = false;
    }
    function busyOff() { var b = $('ppBusy'); if (b) b.hidden = true; }

    // ══ document picker ═════════════════════════════════════════════════════
    var menuOpen = false, optButtons = [], cursor = -1;

    function buildMenu(filter) {
        var list = $('ppDocList'); if (!list) return;
        var q = (filter || '').trim().toLowerCase();
        list.textContent = '';
        optButtons = [];
        var shown = 0;
        GROUPS.forEach(function (g) {
            var ids = g.ids.filter(function (id) {
                if (!q) return true;
                var s = SPECS[id]; if (!s) return false;
                var hay = (docLabel(id) + ' ' + (s.label_en || '') + ' ' + (s.label_ru || '') + ' ' +
                    sizeLabel(s) + ' ' + (g.label || '') + ' ' + (s.country || '')).toLowerCase();
                return hay.indexOf(q) >= 0;
            });
            if (!ids.length) return;
            var h = document.createElement('div');
            h.className = 'pp-docgroup'; h.textContent = g.label;
            list.appendChild(h);
            ids.forEach(function (id) {
                var s = SPECS[id];
                var b = document.createElement('button');
                b.type = 'button'; b.className = 'pp-docopt'; b.setAttribute('role', 'option');
                b.dataset.id = id;
                if (id === st.specId) b.setAttribute('aria-selected', 'true');
                var n = document.createElement('span'); n.className = 'pp-docopt-n'; n.textContent = docLabel(id);
                var sz = document.createElement('span'); sz.className = 'pp-docopt-s'; sz.textContent = sizeLabel(s);
                b.appendChild(n);
                if (specCorner(s)) { var c = document.createElement('span'); c.className = 'pp-docopt-c'; c.textContent = '◢'; c.title = u('corner_label', ''); b.appendChild(c); }
                b.appendChild(sz);
                b.addEventListener('click', function () { pickDoc(id); closeMenu(true); });
                list.appendChild(b);
                optButtons.push(b);
                shown++;
            });
        });
        if (!shown) {
            var e = document.createElement('div');
            e.className = 'tc-empty'; e.textContent = u('doc_search_empty', 'Nothing found');
            list.appendChild(e);
        }
        cursor = -1;
    }

    function openMenu() {
        var m = $('ppDocMenu'), b = $('ppDocBtn'); if (!m) return;
        buildMenu('');
        var s = $('ppDocSearch'); if (s) s.value = '';
        m.hidden = false; menuOpen = true;
        if (b) b.setAttribute('aria-expanded', 'true');
        // Focus the field, but never scroll the page to do it: a picker that
        // yanks the view is the auto-scroll this site does not do.
        if (s) { try { s.focus({ preventScroll: true }); } catch (_) { s.focus(); } }
        feat('doc_menu_open');
    }
    function closeMenu(refocus) {
        var m = $('ppDocMenu'), b = $('ppDocBtn'); if (!m) return;
        m.hidden = true; menuOpen = false;
        if (b) { b.setAttribute('aria-expanded', 'false'); if (refocus) b.focus({ preventScroll: true }); }
    }
    function moveCursor(d) {
        if (!optButtons.length) return;
        cursor = (cursor + d + optButtons.length) % optButtons.length;
        optButtons.forEach(function (b, i) { b.classList.toggle('cur', i === cursor); });
        var el = optButtons[cursor];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    function pickDoc(id, opts) {
        if (!SPECS[id]) return;
        var prev = st.specId;
        st.specId = id;
        var c = specCorner(SPECS[id]);
        // A document's own corner is the STARTING position, not a lock. Changing
        // documents re-applies it, because "student ID → passport" must not
        // quietly leave a stamp corner on a biometric photo.
        st.corner = c
            ? { on: !!c.on, mm: +c.mm || 10, side: c.side || 'right', outline: st.corner.outline }
            : { on: false, mm: 10, side: 'right', outline: st.corner.outline };
        if (SPECS[id].allow_ai_retouch === false && st.bgMode === 'ai') st.bgMode = 'chroma';
        syncDocUi();
        syncCornerUi();
        syncBgUi();
        drawSpecSheet();
        if (!(opts && opts.silent)) {
            try { window.__toolSettings && window.__toolSettings.save('passport-photo', 'doc', id); } catch (_) { }
            if (prev !== id) feat('doc_pick', { doc: id, from: prev || '' });
        }
        if (st.src) render();
    }

    function syncDocUi() {
        var spec = SPECS[st.specId]; if (!spec) return;
        var n = $('ppDocName'), s = $('ppDocSub');
        if (n) n.textContent = docLabel(st.specId);
        if (s) s.textContent = sizeLabel(spec) + ' · ' + spec.target_px_w + '×' + spec.target_px_h + ' px';

        var facts = $('ppFacts');
        if (facts) {
            facts.textContent = '';
            var rows = [
                [u('fact_size', 'Size'), sizeLabel(spec)],
                [u('fact_head', 'Head'), headMmRange(spec)],
                [u('fact_bg', 'Background'), '<span class="pp-swatch" style="background:' + (spec.bg_hex || '#fff') + '"></span>' + (spec.bg_hex || '')],
                [u('fact_dpi', 'Resolution'), spec.dpi ? spec.dpi + ' dpi' : spec.target_px_w + '×' + spec.target_px_h]
            ];
            if (spec.filesize_max_kb) rows.push([u('fact_weight', 'File size'), '≤ ' + spec.filesize_max_kb + ' KB']);
            rows.forEach(function (r) {
                var d = document.createElement('div'); d.className = 'pp-fact';
                var l = document.createElement('div'); l.className = 'pp-fact-l'; l.textContent = r[0];
                var v = document.createElement('div'); v.className = 'pp-fact-v'; v.innerHTML = r[1];
                d.appendChild(l); d.appendChild(v); facts.appendChild(d);
            });
        }

        var custom = $('ppCustomPanel'); if (custom) custom.hidden = st.specId !== CUSTOM;

        var src = $('ppSource');
        if (src) {
            if (spec.source_url) {
                src.href = spec.source_url; src.hidden = false;
                // Only a government page earns the word "official". Half the
                // catalogue is derived from studios and aggregators, and those
                // records already carry verify_note; calling their link
                // "official requirements" would undo the honesty in one word.
                src.textContent = spec.confidence === 'high' ? u('official', '') : u('source_link', '');
            } else { src.hidden = true; }
        }

        var notes = $('ppDocNotes');
        if (notes) {
            notes.textContent = '';
            if (spec.allow_ai_retouch === false) notes.appendChild(msg(u('no_ai_mode', ''), 'warn'));
            if (spec.verify_note) notes.appendChild(msg(u('verify_note', ''), ''));
        }
        var bgName = $('ppBgName'); if (bgName) bgName.textContent = spec.bg_hex || '';
        var dot = $('ppBgDot'); if (dot) dot.style.background = spec.bg_hex || '#fff';
    }

    function msg(text, kind) {
        var p = document.createElement('p');
        p.className = 'tc-msg' + (kind ? ' ' + kind : '');
        p.textContent = text;
        return p;
    }

    // ══ spec sheet — a dimensioned drawing of the document ══════════════════
    /**
     * Not decoration: it answers "what will I get" before a file is chosen,
     * with the same numbers the checklist will later judge the result by. The
     * old build drew an unlabelled silhouette, which showed the aspect ratio
     * and nothing else.
     */
    function drawSpecSheet() {
        var spec = SPECS[st.specId]; if (!spec) return;
        var cv = $('ppExample'); if (!cv) return;
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var PAD = { l: 40, r: 52, t: 20, b: 34 };
        var docW = 200, docH = Math.round(docW * spec.print_h_mm / spec.print_w_mm);
        var W = docW + PAD.l + PAD.r, H = docH + PAD.t + PAD.b;
        cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
        cv.style.aspectRatio = W + ' / ' + H;
        var ctx = cv.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, W, H);

        var x0 = PAD.l, y0 = PAD.t;
        drawDocument(ctx, x0, y0, docW, docH, spec, { corner: st.corner });

        var ink = 'rgba(130,140,158,.95)', fine = '11px system-ui,-apple-system,sans-serif';
        ctx.font = fine; ctx.strokeStyle = ink; ctx.fillStyle = ink; ctx.lineWidth = 1;

        var geo = docGeometry(spec, docH);
        // right side: top margin + head height, the two numbers the office measures
        dimV(ctx, x0 + docW + 12, y0, y0 + geo.crown, fmtMm(spec.head_mode === 'ru_inverted' ? spec.top_margin_mm : null));
        dimV(ctx, x0 + docW + 34, y0 + geo.crown, y0 + geo.chin, headMmRange(spec));
        // left side: full height; bottom: full width
        dimV(ctx, x0 - 14, y0, y0 + docH, fmtMm(spec.print_h_mm));
        dimH(ctx, y0 + docH + 16, x0, x0 + docW, fmtMm(spec.print_w_mm));
        if (st.corner.on) {
            var lw = st.corner.mm / spec.print_w_mm * docW;
            ctx.fillStyle = 'rgba(122,167,255,.95)';
            ctx.textAlign = st.corner.side === 'left' ? 'left' : 'right';
            ctx.fillText(fmtMm(st.corner.mm), st.corner.side === 'left' ? x0 + lw + 3 : x0 + docW - lw - 3, y0 + docH - 5);
            ctx.textAlign = 'start';
        }

        drawSmallExample(spec);
        drawLegend(spec);
    }

    function fmtMm(v) { return v == null ? '' : v + ' ' + u('mm', 'mm'); }

    /** Where the crown, eyes and chin fall inside a frame `h` tall. */
    function docGeometry(spec, h) {
        var headFrac = E ? E.headFraction(spec) : ((spec.head_pct_min + spec.head_pct_max) / 200);
        var crownFrac = E ? E.crownFraction(spec) : 0.1;
        var crown = crownFrac * h, headH = headFrac * h;
        return { crown: crown, chin: crown + headH, eye: crown + 0.46 * headH, headH: headH };
    }

    /** The document itself: backdrop, silhouette, guide lines, corner. */
    function drawDocument(ctx, x, y, w, h, spec, o) {
        o = o || {};
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
        ctx.fillStyle = spec.bg_hex || '#ffffff'; ctx.fillRect(x, y, w, h);

        /* The silhouette exists to make the guide lines mean something, so it
           has to read as a person: hair reaching UP to the crown line (that is
           what "crown" measures — the top of the hair, not of the skull), a
           chin that lands exactly on the chin line, and shoulders close enough
           that the neck does not look stretched. */
        var g = docGeometry(spec, h), cx = x + w / 2;
        var headH = g.headH, headW = headH * 0.70, crown = y + g.crown, chin = y + g.chin, eye = y + g.eye;
        var skin = '#9aa3b4', hair = '#7c8496';

        var shTop = chin + headH * 0.26, shW = headW * 2.45;
        ctx.fillStyle = skin;
        ctx.beginPath();
        ctx.moveTo(cx - shW / 2, y + h + 2);
        ctx.quadraticCurveTo(cx - shW * 0.36, shTop, cx, shTop);
        ctx.quadraticCurveTo(cx + shW * 0.36, shTop, cx + shW / 2, y + h + 2);
        ctx.closePath(); ctx.fill();
        // neck, slightly tapered, drawn before the head so the jaw covers its top
        ctx.beginPath();
        ctx.moveTo(cx - headW * 0.20, chin - headH * 0.12);
        ctx.lineTo(cx + headW * 0.20, chin - headH * 0.12);
        ctx.lineTo(cx + headW * 0.26, shTop + headH * 0.04);
        ctx.lineTo(cx - headW * 0.26, shTop + headH * 0.04);
        ctx.closePath(); ctx.fill();

        var faceTop = crown + headH * 0.13;             // hair occupies the top 13%
        var faceCy = (faceTop + chin) / 2, faceRy = (chin - faceTop) / 2;
        var earY = eye + headH * 0.04, earR = headH * 0.075;
        ctx.beginPath(); ctx.ellipse(cx - headW / 2 + 1, earY, earR * 0.55, earR, 0, 0, 6.2832); ctx.fill();
        ctx.beginPath(); ctx.ellipse(cx + headW / 2 - 1, earY, earR * 0.55, earR, 0, 0, 6.2832); ctx.fill();
        ctx.beginPath(); ctx.ellipse(cx, faceCy, headW / 2, faceRy, 0, 0, 6.2832); ctx.fill();
        // hair cap: reaches the crown line, which is what the dimension measures
        ctx.fillStyle = hair;
        ctx.beginPath();
        ctx.ellipse(cx, faceCy, headW / 2 + 1, faceRy + (faceTop - crown), 0, Math.PI, 2 * Math.PI);
        ctx.closePath(); ctx.fill();

        function guide(yy, col) {
            ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
            ctx.beginPath(); ctx.moveTo(x, yy); ctx.lineTo(x + w, yy); ctx.stroke(); ctx.setLineDash([]);
        }
        guide(crown, 'rgba(34,197,94,.9)');
        guide(chin, 'rgba(245,158,11,.9)');
        guide(eye, 'rgba(56,189,248,.8)');

        var c = o.corner;
        if (c && c.on && c.mm > 0) {
            var lw = c.mm / spec.print_w_mm * w, lh = c.mm / spec.print_h_mm * h;
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            if (c.side === 'left') { ctx.moveTo(x, y + h); ctx.lineTo(x + lw, y + h); ctx.lineTo(x, y + h - lh); }
            else { ctx.moveTo(x + w, y + h); ctx.lineTo(x + w - lw, y + h); ctx.lineTo(x + w, y + h - lh); }
            ctx.closePath(); ctx.fill();
            ctx.strokeStyle = 'rgba(122,167,255,.95)'; ctx.lineWidth = 1.2; ctx.setLineDash([3, 2]);
            ctx.beginPath();
            if (c.side === 'left') { ctx.moveTo(x + lw, y + h); ctx.lineTo(x, y + h - lh); }
            else { ctx.moveTo(x + w - lw, y + h); ctx.lineTo(x + w, y + h - lh); }
            ctx.stroke(); ctx.setLineDash([]);
        }
        ctx.restore();
        ctx.strokeStyle = 'rgba(0,0,0,.30)'; ctx.lineWidth = 1;
        ctx.strokeRect(x + .5, y + .5, w - 1, h - 1);
    }

    /* Dimension lines in the style a drawing uses: a witness line with arrows
       and the number beside it. Numbers are drawn with the locale's own unit,
       because "mm"/"мм"/"مم" are not the same three characters. */
    function dimV(ctx, x, y1, y2, label) {
        if (!label || y2 - y1 < 4) return;
        ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x, y2); ctx.stroke();
        arrow(ctx, x, y1, 0, 1); arrow(ctx, x, y2, 0, -1);
        ctx.save(); ctx.translate(x - 4, (y1 + y2) / 2); ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        ctx.fillText(label, 0, 0); ctx.restore();
        ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
    }
    function dimH(ctx, y, x1, x2, label) {
        if (!label) return;
        ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
        arrow(ctx, x1, y, 1, 0); arrow(ctx, x2, y, -1, 0);
        ctx.textAlign = 'center'; ctx.textBaseline = 'top';
        ctx.fillText(label, (x1 + x2) / 2, y + 3);
        ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
    }
    function arrow(ctx, x, y, dx, dy) {
        var s = 3.5;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + dx * s * 1.7 - dy * s * .6, y + dy * s * 1.7 - dx * s * .6);
        ctx.lineTo(x + dx * s * 1.7 + dy * s * .6, y + dy * s * 1.7 + dx * s * .6);
        ctx.closePath(); ctx.fill();
    }

    function drawSmallExample(spec) {
        var cv = $('ppExampleSmall'); if (!cv) return;
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var w = 172, h = Math.round(w * spec.print_h_mm / spec.print_w_mm);
        cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
        cv.style.width = w + 'px'; cv.style.height = h + 'px';
        var ctx = cv.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        drawDocument(ctx, 0, 0, w, h, spec, { corner: st.corner });
    }

    function drawLegend(spec) {
        var el = $('ppLegend'); if (!el) return;
        el.textContent = '';
        var items = [
            ['rgba(34,197,94,.9)', u('legend_crown', 'crown')],
            ['rgba(245,158,11,.9)', u('legend_chin', 'chin')],
            ['rgba(56,189,248,.8)', u('legend_eye', 'eye line')]
        ];
        if (st.corner.on) items.push(['rgba(122,167,255,.95)', u('legend_corner', 'corner') + ' — ' + fmtMm(st.corner.mm)]);
        items.forEach(function (it) {
            var li = document.createElement('li');
            var k = document.createElement('span'); k.className = 'pp-key'; k.style.color = it[0];
            var s = document.createElement('span'); s.textContent = it[1];
            li.appendChild(k); li.appendChild(s); el.appendChild(li);
        });
        if (spec.color_mode === 'color-or-bw') {
            var li2 = document.createElement('li');
            var k2 = document.createElement('span'); k2.className = 'pp-key solid'; k2.style.color = 'var(--lg-text-3)';
            var s2 = document.createElement('span'); s2.textContent = u('legend_bw', '');
            li2.appendChild(k2); li2.appendChild(s2); el.appendChild(li2);
        }
    }

    // ══ pipeline ════════════════════════════════════════════════════════════
    function curGeom() {
        var g = Object.assign({}, st.geom);
        g.chin = Object.assign({}, st.geom.chin);
        if (st.manualChinY != null) g.chin.y = st.manualChinY;
        g.faceH = g.chin.y - g.ovalTop.y;
        return g;
    }
    function curCrownY(g) { return (st.manualCrownY != null) ? st.manualCrownY : E.estimateCrownY(g, st.k); }

    function recompute() {
        if (!st.src || !st.geom || !st.specId) return null;
        var spec = SPECS[st.specId], g = curGeom(), crownY = curCrownY(g);
        st._g = g; st._crownY = crownY;
        st.tr = E.computeTransform(g, crownY, spec, {
            deTilt: st.deTilt, panX: st.panX, panY: st.panY, zoom: st.zoom, rotateExtra: st.rotateExtra
        });
        return spec;
    }

    function paintPreview(rc) {
        var pv = $('ppPreview'); if (!pv) return;
        var ps = Math.min(MAX_PREV / rc.height, 1);
        var w = Math.round(rc.width * ps), h = Math.round(rc.height * ps);
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        pv.width = Math.round(w * dpr); pv.height = Math.round(h * dpr);
        pv.style.width = w + 'px'; pv.style.height = h + 'px';
        var c = pv.getContext('2d');
        c.imageSmoothingQuality = 'high';
        c.drawImage(rc, 0, 0, pv.width, pv.height);

        /* A white corner on a white document is INVISIBLE, and that is not a
           bug in the corner — it is what a blank corner looks like. But a
           feature you cannot see is a feature you cannot trust, so the preview
           draws the edge as a guide, the same way the editor draws the crop
           frame. It is a guide and never bytes: applyCorner has already run and
           the export is untouched by anything below this line. */
        if (st.corner.on && st.corner.mm > 0) {
            var W = pv.width, H = pv.height;
            var lw = st.corner.mm / SPECS[st.specId].print_w_mm * W;
            var lh = st.corner.mm / SPECS[st.specId].print_h_mm * H;
            c.save();
            c.strokeStyle = 'rgba(122,167,255,.95)';
            c.lineWidth = Math.max(1, dpr);
            c.setLineDash([5 * dpr, 4 * dpr]);
            c.beginPath();
            if (st.corner.side === 'left') { c.moveTo(0, H - lh); c.lineTo(lw, H); }
            else { c.moveTo(W - lw, H); c.lineTo(W, H - lh); }
            c.stroke();
            c.restore();
        }
    }

    /**
     * Give a canvas the pipeline no longer holds back to the browser. WebKit
     * returns canvas memory only when the canvas is sized 0×0 — a dropped
     * reference keeps it until the collector comes — and the canvas budget of a
     * tab process is shared and small on iOS. A drag here mints two to four
     * canvases per frame (crop, greyscale, resample, corner); before the
     * collector catches up the next one is created without a buffer, and toBlob
     * answers null (Sentry QE). Live state is never touched.
     */
    function retire(c) {
        if (!c || c === st.src || c === st._base || c === st._bgApplied || c === st._rendered) return;
        try { c.width = c.height = 0; } catch (_) { }
    }

    /**
     * The file could not be made. A refused encode — `why: 'null'` from the
     * shared encoder, or a canvas WebKit never gave memory to — is the device
     * out of canvas memory, and the person reads the site's one sentence for
     * that instead of a button that silently does nothing. Anything else goes
     * through the shared door, which picks the words.
     */
    function exportRefused(e) {
        var mem = !!(e && e.code === 'CANVAS_ENCODE_FAILED' && (e.why === 'null' || e.why === 'no_buffer'));
        if (window.__handleError) window.__handleError(e, 'pp_export', { say: mem ? (window.__MEM_ERR || '') : '' });
    }

    /**
     * One pass: crop (at matting resolution) → background → greyscale →
     * downsample to the print size → corner. The corner is last and at final
     * size on purpose — it is the edge a person lines a stamp up against, and
     * a triangle drawn big and then resampled arrives soft.
     */
    function render() {
        var spec = recompute(); if (!spec) return;
        /* Both background lanes get the extra resolution, not just the matte:
           generic-3x4 outputs 354x472, and deciding a background on a frame
           that small costs the silhouette — measured on the same corpus, the
           shadow left behind is 7.7 levels at output size against 4.0 at
           mattingScale. Everything downstream is scale-agnostic, because
           finish() resamples before the corner is drawn.

           But the INTERACTIVE path never pays for it. render() runs on every
           pointer frame and on every pixel of slider travel, and at mattingScale
           a render+resample costs 26 ms against 3.5 (3000x4000 source, k=2.97,
           measured) — so the crop a person is pushing around would stutter in
           exchange for resolution nobody is looking at yet. The AI lane has been
           paying exactly that since it shipped: the reporter of the shadow
           defect has two inp_slow events in their session, 424 ms on the zoom
           slider and 480 ms on the canvas, both with the AI lane on. So the
           preview is always built at output size and the big frame is built
           once, inside applyHeavy, which is debounced already. */
        var k = 1, oldBase = st._base, oldBg = st._bgApplied;
        st._base = E.render(st.src, st.tr, spec, k);
        st._bgApplied = st._base;      // shown immediately; replaced when the lane finishes
        finish(spec);
        retire(oldBase); retire(oldBg);
        drawSource();
        if (st.bgMode !== 'none' && !st.drag) scheduleHeavy();
    }

    /**
     * Everything after the crop that is cheap enough to run on every frame.
     *
     * It reads `_bgApplied`, NOT `_base`. Reading the raw crop here is the bug
     * this comment exists to prevent: toggling the corner or greyscale would
     * silently throw away a background the person had just waited seconds for,
     * and the only symptom is the old backdrop coming back.
     */
    function finish(spec) {
        var src = st._bgApplied || st._base;
        if (!src) return;
        var prev = st._rendered, out = src, step;
        if (st.gray) out = E.toGrayscale(out);
        step = out; out = E.resample(out, spec.target_px_w, spec.target_px_h);
        if (out !== step) retire(step);
        step = out; out = E.applyCorner(out, spec, st.corner);
        if (out !== step) retire(step);
        st._rendered = out;
        if (prev !== out) retire(prev);
        paintPreview(out);
        updateOutSize(spec);
        updateChecklist();
    }

    /**
     * A slider fires `input` per pixel of travel, and the AI lane is seconds of
     * inference per call. Without this the zoom slider queues one full matte
     * per mouse movement — the tokens below would discard the results, but the
     * work would still be done, one run at a time, for as long as it took.
     */
    var _heavyTimer = null;
    function scheduleHeavy() {
        if (_heavyTimer) clearTimeout(_heavyTimer);
        var delay = st.bgMode === 'ai' ? 420 : 110;
        _heavyTimer = setTimeout(function () { _heavyTimer = null; applyHeavy(); }, delay);
    }

    var _heavyToken = 0, _matteReady = false;
    async function applyHeavy() {
        if (!st._base) return;
        var token = ++_heavyToken, spec = SPECS[st.specId];
        if (st.bgMode === 'none') { st._bgApplied = st._base; finish(spec); return; }
        var note = $('ppBgNote');
        try {
            if (st.bgMode === 'ai') {
                // The full-screen overlay belongs to the ONE wait nobody can
                // predict — the first download. Every later run is a couple of
                // seconds and gets a line in the panel instead of a curtain.
                if (!_matteReady) {
                    var cached = await E.mattingCached();
                    if (!cached) busy(u('bg_ai_download', ''), u('bg_ai_size', ''), 1);
                    else busy(u('bg_ai_working', ''), '', null);
                } else if (note) { note.textContent = u('bg_ai_working', ''); }
            }
            /* The one place the big frame is worth building: this runs
               debounced and at most once per settled gesture, where render()
               runs on every frame of the gesture itself. */
            var base = E.render(st.src, st.tr, spec, E.mattingScale(st.tr));
            var out = await E.replaceBackground(base, st.bgMode, spec, {
                onWall: function (a) { st._bgPartial = a && a.coverage < 0.12; },
                onProgress: function (p) {
                    if (!p) return;
                    if (p.stage === 'model-download' && p.total) busy(u('bg_ai_download', ''), u('bg_ai_size', ''), p.loaded / p.total * 100);
                    else if (p.stage === 'session') busy(u('bg_ai_working', ''), '', 100);
                }
            });
            if (token !== _heavyToken) { if (out !== base) retire(out); retire(base); return; }
            busyOff();
            if (st.bgMode === 'ai') _matteReady = true;
            var oldBg = st._bgApplied;
            st._bgApplied = out;
            finish(spec);
            retire(oldBg);
            if (out !== base) retire(base);
            syncBgUi();
            feat('bg_applied', { mode: st.bgMode, doc: st.specId });
        } catch (e) {
            if (token !== _heavyToken) return;
            busyOff();
            var m = String((e && e.message) || e);
            window.__toast && window.__toast(u('err_bg', 'Background processing failed'), 'error');
            try { window.__aeToolError && window.__aeToolError({ error_type: 'bg_failed', msg: m.slice(0, 140), mode: st.bgMode }); } catch (_) { }
            st.bgMode = 'none'; syncBgUi();
            st._bgApplied = st._base;
            finish(spec);
        }
    }

    function updateOutSize(spec) {
        var el = $('ppOutSize'); if (!el) return;
        var parts = [sizeLabel(spec), spec.target_px_w + '×' + spec.target_px_h + ' px'];
        if (spec.dpi) parts.push(spec.dpi + ' dpi');
        if (st.corner.on) parts.push(u('corner_label', 'corner') + ' ' + fmtMm(st.corner.mm));
        el.textContent = parts.join(' · ');
        // Say out loud that the dashed edge on the preview is a guide. Without
        // this line the honest way to show an invisible white corner becomes a
        // new question: "will that line be printed on my photo?"
        if (st.corner.on) {
            var g = document.createElement('span');
            g.style.display = 'block'; g.style.marginTop = '3px';
            g.textContent = u('preview_guide', '');
            el.appendChild(g);
        }
        var sub = $('ppDlSub'); if (sub) sub.textContent = sizeLabel(spec);
    }

    var _clToken = 0;
    async function updateChecklist() {
        if (!st._a || !st._rendered || !st.tr) return;
        var token = ++_clToken, spec = SPECS[st.specId];
        /* The JPEG is encoded only for the «file size» row, and only a document
           with a size cap has that row (evaluateCompliance reads `exp` under
           filesize_max_kb alone). Every other spec — all the Russian ones —
           paid a full JPEG encode per frame of a drag for nothing, and that idle
           encode is where a refusing encoder killed the checklist (Sentry QE).
           A refusal is no reason to leave the list unanswered either: the row is
           left out, and Download says why when the person asks for the file. */
        var exp = null;
        if (spec && spec.filesize_max_kb) {
            try { exp = await E.exportJpegSizeBand(st._rendered, spec); }
            catch (_) { exp = null; }
        }
        if (token !== _clToken || !st._a || !st._rendered || !st.tr) return;
        var rows = E.evaluateCompliance(st._a, st.tr, spec, st._rendered, exp, st.deTilt,
            { corner: st.corner, gray: st.gray, mm: u('mm', 'mm') });
        st._checklist = rows;
        window.__ppChecklist = function () { return rows; };
        paintChecklist(rows);
    }

    function paintChecklist(rows) {
        var el = $('ppChecklist'); if (!el) return;
        var dot = { pass: '#22c55e', warn: '#f59e0b', fail: '#ef4444', info: '#64748b' };
        el.textContent = '';
        rows.forEach(function (r) {
            var row = document.createElement('div'); row.className = 'pp-cl-row';
            var d = document.createElement('span'); d.className = 'pp-cl-dot';
            d.style.background = dot[r.status] || '#888';
            var l = document.createElement('span'); l.className = 'pp-cl-label';
            l.textContent = cl(r.id, r.id);
            if (r.detail) {
                var s = document.createElement('span'); s.className = 'pp-cl-detail';
                s.textContent = ' — ' + r.detail; l.appendChild(s);
            }
            row.appendChild(d); row.appendChild(l); el.appendChild(row);
        });
        var bad = rows.filter(function (r) { return r.status === 'fail'; }).length;
        var warn = rows.filter(function (r) { return r.status === 'warn'; }).length;
        var v = $('ppVerdict');
        if (v) {
            v.className = 'pp-verdict ' + (bad ? 'bad' : (warn ? 'warn' : 'ok'));
            v.querySelector('.pp-verdict-b').textContent = bad
                ? u('verdict_fail', '').replace(':n', bad)
                : (warn ? u('verdict_warn', '').replace(':n', warn) : u('verdict_ok', ''));
        }
        var note = $('ppClNote'); if (note) note.textContent = cl('note', '');
    }

    // ── source view: crop frame, dimmed surroundings, draggable guides ───────
    function drawSource() {
        var dc = $('ppCanvas'); if (!dc || !st.src) return;
        var W = st.src.width, H = st.src.height, sc = st.dispScale;
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var cssW = Math.round(W * sc), cssH = Math.round(H * sc);
        dc.width = Math.round(cssW * dpr); dc.height = Math.round(cssH * dpr);
        dc.style.aspectRatio = cssW + ' / ' + cssH;
        var ctx = dc.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cssW, cssH);
        ctx.drawImage(st.src, 0, 0, cssW, cssH);

        var m = st.tr.matrix, det = m[0] * m[3] - m[1] * m[2];
        function inv(x, y) {
            var ia = m[3] / det, ib = -m[1] / det, ic = -m[2] / det, id = m[0] / det,
                ie = (m[2] * m[5] - m[3] * m[4]) / det, iff = (m[1] * m[4] - m[0] * m[5]) / det;
            return { x: (ia * x + ic * y + ie) * sc, y: (ib * x + id * y + iff) * sc };
        }
        var TW = st.tr.TW, TH = st.tr.TH;
        var q = [inv(0, 0), inv(TW, 0), inv(TW, TH), inv(0, TH)];

        // Dim everything the crop throws away. A blue outline alone left people
        // guessing which side of the line survives.
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, cssW, cssH);
        ctx.moveTo(q[0].x, q[0].y);
        for (var i = 3; i >= 1; i--) ctx.lineTo(q[i].x, q[i].y);
        ctx.closePath();
        ctx.fillStyle = 'rgba(8,10,16,.58)';
        ctx.fill('evenodd');
        ctx.restore();

        ctx.strokeStyle = 'rgba(122,167,255,.95)'; ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(q[0].x, q[0].y); ctx.lineTo(q[1].x, q[1].y);
        ctx.lineTo(q[2].x, q[2].y); ctx.lineTo(q[3].x, q[3].y);
        ctx.closePath(); ctx.stroke();

        guideLine(ctx, st._crownY * sc, '#22c55e', u('legend_crown', 'crown'), cssW);
        guideLine(ctx, st._g.chin.y * sc, '#f59e0b', u('legend_chin', 'chin'), cssW);
        ctx.fillStyle = 'rgba(239,68,68,.95)';
        ctx.beginPath(); ctx.arc(st._g.eyeX * sc, st._g.eyeY * sc, 3.5, 0, 6.29); ctx.fill();
    }

    /** A dashed rule with a grip you can see — the old build drew a 20×8 px
     *  rectangle with no affordance, and nothing said the line could be moved. */
    function guideLine(ctx, y, color, label, w) {
        ctx.strokeStyle = color; ctx.lineWidth = 1.6; ctx.setLineDash([7, 5]);
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); ctx.setLineDash([]);
        ctx.font = '600 11px system-ui,-apple-system,sans-serif';
        var tw = ctx.measureText(label).width;
        ctx.fillStyle = color;
        roundRect(ctx, 5, y - 9, tw + 12, 18, 9); ctx.fill();
        ctx.fillStyle = '#0b0d12'; ctx.textBaseline = 'middle';
        ctx.fillText(label, 11, y + .5);
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = color;
        roundRect(ctx, w - 26, y - 5, 21, 10, 5); ctx.fill();
    }
    function roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
        ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
    }

    // ── pointer ─────────────────────────────────────────────────────────────
    function evtPos(e) {
        var dc = $('ppCanvas'), r = dc.getBoundingClientRect();
        var cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
        var cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
        var W = st.src ? st.src.width * st.dispScale : r.width;
        var H = st.src ? st.src.height * st.dispScale : r.height;
        return { x: (r.width ? cx * (W / r.width) : cx) / st.dispScale,
                 y: (r.height ? cy * (H / r.height) : cy) / st.dispScale,
                 nx: r.width ? cx / r.width : 0, ny: r.height ? cy / r.height : 0 };
    }
    function onDown(e) {
        if (!st.src) return;
        var p = evtPos(e);
        if (st._pointMode) { e.preventDefault(); findAt(p.nx, p.ny); return; }
        if (!st._g) return;
        var dChin = Math.abs(p.y - st._g.chin.y) * st.dispScale, dCrown = Math.abs(p.y - st._crownY) * st.dispScale;
        if (dCrown < HIT && dCrown <= dChin) st.drag = 'crown';
        else if (dChin < HIT) st.drag = 'chin';
        else { st.drag = 'pan'; st._panStart = { sx: p.x, sy: p.y, panX: st.panX, panY: st.panY }; }
        e.preventDefault();
        feat('handle_drag', { which: st.drag });
    }
    function onMove(e) {
        if (!st.drag) return;
        e.preventDefault();
        var p = evtPos(e);
        if (st.drag === 'pan') {
            var s = st.tr ? st.tr.s : 1;
            st.panX = st._panStart.panX - (p.x - st._panStart.sx) * s;
            st.panY = st._panStart.panY - (p.y - st._panStart.sy) * s;
            render(); return;
        }
        var sy = Math.max(0, Math.min(st.src.height, p.y));
        if (st.drag === 'crown') st.manualCrownY = sy; else st.manualChinY = sy;
        render();
    }
    function onUp() { if (st.drag) { st.drag = null; if (st.bgMode !== 'none') applyHeavy(); } }

    // ── loading a photo ─────────────────────────────────────────────────────
    async function loadFile(blob) {
        stage('uploading');
        var ready = E && E.ready && E.ready();
        busy(ready ? u('analyzing', '') : u('loading_model', ''), '', null);
        var a;
        try {
            a = await E.analyze(blob, {
                onStep: function (done, total) {
                    busy(u('searching_face', ''), (done + 1) + ' / ' + total, (done + 1) / total * 100);
                }
            });
        } catch (e) {
            busyOff();
            var unavailable = !!(window.FRLandmarks && window.FRLandmarks.isUnavailable && window.FRLandmarks.isUnavailable(e));
            window.__toast && window.__toast(unavailable ? u('err_detector', '') : u('err_read', ''), 'error');
            try { window.__aeToolError && window.__aeToolError({ error_type: unavailable ? 'detector_unavailable' : 'analyze_threw', msg: String((e && e.message) || e).slice(0, 140) }); } catch (_) { }
            return false;
        }
        busyOff();
        if (!a.ok) {
            // Keep the photo. It is on screen, the person can point at the face,
            // and "no face" is a question the tool has one more way to answer.
            st.src = a.canvas;
            st.dispScale = Math.min(MAX_DISP / a.canvas.width, MAX_DISP / a.canvas.height, 1);
            offerPointMode(a);
            try { window.__aeToolError && window.__aeToolError({ error_type: 'analyze_failed', faces: a.faces, via: a.via, tries: a.tries, w: a.W, h: a.H }); } catch (_) { }
            return false;
        }
        adopt(a);
        feat('photo_ready', { via: a.via, tries: a.tries, doc: st.specId });
        stage('aligned');
        return true;
    }

    function adopt(a) {
        st.src = a.canvas; st.geom = a.geom; st._a = a;
        st.manualChinY = null; st.manualCrownY = null; st.k = 0.12;
        st.panX = 0; st.panY = 0; st.zoom = 1; st.rotateExtra = 0;
        setRange('ppHair', 12); setRange('ppZoom', 100); setRange('ppRotate', 0);
        st.dispScale = Math.min(MAX_DISP / st.src.width, MAX_DISP / st.src.height, 1);
        st._pointMode = false;
        showWorkspace(true);
        render();
    }

    function offerPointMode(a) {
        showWorkspace(true, true);
        st._pointMode = true;
        var dc = $('ppCanvas');
        if (dc) {
            var dpr = Math.min(2, window.devicePixelRatio || 1);
            var cssW = Math.round(a.canvas.width * st.dispScale), cssH = Math.round(a.canvas.height * st.dispScale);
            dc.width = Math.round(cssW * dpr); dc.height = Math.round(cssH * dpr);
            dc.style.aspectRatio = cssW + ' / ' + cssH;
            var ctx = dc.getContext('2d');
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.drawImage(a.canvas, 0, 0, cssW, cssH);
        }
        var p = $('ppEditorPanel');
        if (p && !p.querySelector('.pp-pointmsg')) {
            var m = msg(a.faces > 1 ? u('err_one_face', '') : u('point_at_face', ''), 'warn');
            m.classList.add('pp-pointmsg');
            p.appendChild(m);
        }
        window.__toast && window.__toast(a.faces > 1 ? u('err_one_face', '') : u('point_at_face', ''), 'warning');
    }

    async function findAt(nx, ny) {
        busy(u('searching_face', ''), '', null);
        var a;
        try { a = await E.analyzeAt(st.src, nx, ny); }
        catch (e) { busyOff(); window.__toast && window.__toast(u('err_read', ''), 'error'); return; }
        busyOff();
        if (!a.ok) { window.__toast && window.__toast(u('err_no_face', ''), 'error'); feat('point_miss'); return; }
        var m = document.querySelector('.pp-pointmsg'); if (m) m.remove();
        adopt(a);
        feat('point_hit', { via: a.via });
        stage('aligned');
    }

    function showWorkspace(on, partial) {
        ['ppFramePanel', 'ppBgPanel', 'ppWork', 'ppExportPanel'].forEach(function (id) {
            var el = $(id); if (el) el.hidden = !on || !!partial;
        });
        var ed = $('ppEditorPanel'); if (ed) ed.hidden = !on;
        var sheet = $('ppSpecSheet'); if (sheet) sheet.hidden = !!on && !partial;
        var empty = $('ppResultEmpty'); if (empty) empty.hidden = !!on && !partial;
    }

    function clearPhoto() {
        _clToken++;   // a checklist still awaiting its JPEG must not evaluate a photo that is gone (Sentry TIMBRICA-FRONTEND-KH)
        st.src = null; st.geom = null; st._a = null; st._rendered = null; st._base = null; st._bgApplied = null;
        st._pointMode = false;
        var m = document.querySelector('.pp-pointmsg'); if (m) m.remove();
        showWorkspace(false);
        drawSpecSheet();
    }

    // ── control sync ────────────────────────────────────────────────────────
    function setRange(id, v) { var el = $(id); if (el) { el.value = String(v); } }

    function syncBgUi() {
        var spec = SPECS[st.specId];
        ['ppBgNone', 'ppBgChroma', 'ppBgAi'].forEach(function (id) {
            var b = $(id); if (b) b.classList.toggle('on', b.dataset.bg === st.bgMode);
        });
        var ai = $('ppBgAi');
        if (ai) {
            var noAi = spec && spec.allow_ai_retouch === false;
            ai.disabled = !!noAi;
            ai.style.opacity = noAi ? '.45' : '';
            ai.title = noAi ? u('no_ai_mode', '') : '';
        }
        var note = $('ppBgNote');
        if (note) {
            /* `bg_partial` replaces the tolerance slider that used to live here.
               Sweeping it across its whole range changed nothing measurable —
               the wall is now found by structure, not by a threshold — so the
               honest control is no control and a line that says when the wall
               was only partly found. A knob that cannot move the result is
               worse than none: it is what the first reporter of this defect
               spent two and a half minutes pulling back and forth. */
            note.textContent = st.bgMode === 'ai' ? u('bg_ai_note', '')
                : (st.bgMode === 'chroma' ? ((st._bgPartial ? u('bg_partial', '') + ' ' : '') + u('bg_chroma_note', '')) : u('bg_none_note', ''));
        }
    }

    function syncCornerUi() {
        var spec = SPECS[st.specId];
        var box = $('ppCornerBox');
        var allowed = !!specCorner(spec);
        if (box) box.hidden = !allowed;
        if (!allowed) return;
        document.querySelectorAll('[data-corner]').forEach(function (b) {
            var want = st.corner.on ? st.corner.side : 'off';
            b.classList.toggle('on', b.dataset.corner === want);
        });
        var opts = $('ppCornerOpts'); if (opts) opts.hidden = !st.corner.on;
        setRange('ppCornerMm', st.corner.mm);
        var v = $('ppCornerMmV'); if (v) v.textContent = fmtMm(st.corner.mm);
        var line = $('ppCornerLine'); if (line) line.checked = !!st.corner.outline;
        var demo = $('ppCornerDemo');
        if (demo) {
            var i = demo.querySelector('i');
            if (i) i.className = st.corner.side === 'left' ? 'l' : 'r';
        }
    }

    // ══ wiring ══════════════════════════════════════════════════════════════
    function wire() {
        E = window.PassportPhotoEngine;
        if (!E) return;
        E.configure({ sources: window.__PP_SOURCES, matting: window.__PP_MATTING });

        // «Свой размер» joins the catalogue as an ordinary record, last.
        var savedCustom = null;
        try { savedCustom = window.__toolSettings && window.__toolSettings.load('passport-photo', 'custom', null); } catch (_) { }
        var cv = Object.assign({}, CUSTOM_DEFAULT, savedCustom || {});
        ['ppCW:w', 'ppCH:h', 'ppCHmin:headMin', 'ppCHmax:headMax', 'ppCTop:top', 'ppCBg:bg'].forEach(function (pair) {
            var p = pair.split(':'), el = $(p[0]);
            if (el && cv[p[1]] != null) el.value = cv[p[1]];
        });
        SPECS[CUSTOM] = buildCustomSpec(cv);
        GROUPS = GROUPS.concat([{ key: CUSTOM, label: (T.groups && T.groups.custom) || u('custom_title', 'Custom'), ids: [CUSTOM] }]);

        // The document the person used last time. Restored silently: a
        // remembered choice is not a choice they are making now.
        try {
            var saved = window.__toolSettings && window.__toolSettings.load('passport-photo', 'doc', null);
            if (saved && SPECS[saved]) st.specId = saved;
        } catch (_) { }

        pickDoc(st.specId, { silent: true });

        ['ppCW', 'ppCH', 'ppCHmin', 'ppCHmax', 'ppCTop', 'ppCBg'].forEach(function (id) {
            var el = $(id); if (!el) return;
            el.addEventListener('input', function () {
                var v = customValues();
                SPECS[CUSTOM] = buildCustomSpec(v);
                try { window.__toolSettings && window.__toolSettings.save('passport-photo', 'custom', v); } catch (_) { }
                if (st.specId !== CUSTOM) return;
                syncDocUi(); drawSpecSheet();
                if (st.src) render();
            });
            el.addEventListener('change', function () { feat('custom_size', customValues()); });
        });

        // picker
        var btn = $('ppDocBtn');
        if (btn) btn.addEventListener('click', function () { menuOpen ? closeMenu(true) : openMenu(); });
        var search = $('ppDocSearch');
        if (search) {
            search.addEventListener('input', function () { buildMenu(search.value); });
            search.addEventListener('keydown', function (e) {
                if (e.key === 'ArrowDown') { e.preventDefault(); moveCursor(1); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); moveCursor(-1); }
                else if (e.key === 'Enter') {
                    e.preventDefault();
                    var pick = optButtons[cursor >= 0 ? cursor : 0];
                    if (pick) { pickDoc(pick.dataset.id); closeMenu(true); }
                } else if (e.key === 'Escape') { e.preventDefault(); closeMenu(true); }
            });
        }
        document.addEventListener('click', function (e) {
            if (!menuOpen) return;
            var pop = $('ppDocMenu'), b = $('ppDocBtn');
            if (pop && !pop.contains(e.target) && b && !b.contains(e.target)) closeMenu(false);
        });
        document.addEventListener('keydown', function (e) { if (menuOpen && e.key === 'Escape') closeMenu(true); });

        // upload
        var up = $('ppUpload');
        if (up) {
            up.addEventListener('tool-uploader:file', function (e) {
                var f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
                if (f) loadFile(f);
            });
            up.addEventListener('tool-uploader:clear', clearPhoto);
        }
        var rePh = $('ppResultEmpty');
        if (rePh) rePh.addEventListener('click', function () {
            var inp = document.querySelector('[data-uploader-for="ppUpload"]');
            if (inp) inp.click();
        });

        // frame controls
        bindRange('ppHair', 'ppHairV', function (v) { st.k = v / 100; st.manualCrownY = null; }, function (v) { return v + '%'; });
        bindRange('ppZoom', 'ppZoomV', function (v) { st.zoom = v / 100; }, function (v) { return v + '%'; });
        bindRange('ppRotate', 'ppRotateV', function (v) { st.rotateExtra = v; }, function (v) { return v + '°'; });
        var tilt = $('ppTilt');
        if (tilt) tilt.addEventListener('change', function () { st.deTilt = tilt.checked; if (st.src) render(); feat('detilt', { on: tilt.checked ? 1 : 0 }); });
        var reset = $('ppReset');
        if (reset) reset.addEventListener('click', function () {
            st.manualChinY = null; st.manualCrownY = null; st.k = 0.12;
            st.panX = 0; st.panY = 0; st.zoom = 1; st.rotateExtra = 0;
            setRange('ppHair', 12); setRange('ppZoom', 100); setRange('ppRotate', 0);
            syncRangeLabels();
            if (st.src) render();
            feat('reset');
        });

        // background
        ['ppBgNone', 'ppBgChroma', 'ppBgAi'].forEach(function (id) {
            var b = $(id); if (!b) return;
            b.addEventListener('click', function () {
                if (b.disabled) return;
                st.bgMode = b.dataset.bg;
                syncBgUi();
                feat('bg_mode', { mode: st.bgMode });
                if (st.src) { if (st.bgMode === 'none') { st._bgApplied = st._base; finish(SPECS[st.specId]); } else applyHeavy(); }
            });
        });
        // corner
        document.querySelectorAll('[data-corner]').forEach(function (b) {
            b.addEventListener('click', function () {
                var mode = b.dataset.corner;
                if (mode === 'off') st.corner.on = false;
                else { st.corner.on = true; st.corner.side = mode; }
                syncCornerUi(); drawSpecSheet();
                if (st.src) finish(SPECS[st.specId]);
                feat('corner', { on: st.corner.on ? 1 : 0, side: st.corner.side, mm: st.corner.mm, doc: st.specId });
            });
        });
        var cmm = $('ppCornerMm');
        if (cmm) cmm.addEventListener('input', function () {
            st.corner.mm = +cmm.value;
            var v = $('ppCornerMmV'); if (v) v.textContent = fmtMm(st.corner.mm);
            drawSpecSheet();
            if (st.src) finish(SPECS[st.specId]);
        });
        var cline = $('ppCornerLine');
        if (cline) cline.addEventListener('change', function () {
            st.corner.outline = cline.checked;
            if (st.src) finish(SPECS[st.specId]);
            feat('corner_outline', { on: cline.checked ? 1 : 0 });
        });

        // greyscale
        var gray = $('ppGray');
        if (gray) gray.addEventListener('change', function () {
            st.gray = gray.checked;
            if (st.src) { if (st.bgMode === 'none') { st._bgApplied = st._base; finish(SPECS[st.specId]); } else applyHeavy(); }
            feat('grayscale', { on: gray.checked ? 1 : 0 });
        });

        // canvas
        var dc = $('ppCanvas');
        if (dc) {
            dc.addEventListener('pointerdown', onDown);
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
        }

        // export
        var dl = $('ppDownload');
        if (dl) dl.addEventListener('click', async function () {
            if (!st._rendered) return;
            var spec = SPECS[st.specId], exp;
            try { exp = await E.exportJpegSizeBand(st._rendered, spec); }
            catch (e) { exportRefused(e); return; }
            var name = 'photo-' + st.specId + (st.corner.on ? '-corner' : '') + '.jpg';
            if (window.__downloadFile) window.__downloadFile(exp.blob, name);
            try {
                window.__aeToolComplete && window.__aeToolComplete({
                    doc: st.specId, kind: 'jpeg', sizeKB: exp.sizeKB, bg: st.bgMode,
                    corner: st.corner.on ? st.corner.mm : 0, corner_side: st.corner.on ? st.corner.side : '',
                    gray: st.gray ? 1 : 0, warns: (st._checklist || []).filter(function (r) { return r.status !== 'pass' && r.status !== 'info'; }).length
                });
            } catch (_) { }
        });
        var pr = $('ppPrint');
        if (pr) pr.addEventListener('click', async function () {
            if (!st._rendered || !window.PassportPrintSheet) return;
            pr.disabled = true;
            try {
                var spec = SPECS[st.specId], exp = await E.exportJpegSizeBand(st._rendered, spec);
                var lines = $('ppSheetLines');
                var meta = await window.PassportPrintSheet.build(exp.blob, spec, {
                    sheet: $('ppSheet').value,
                    count: parseInt($('ppCount').value, 10) || null,
                    lines: !lines || lines.checked
                });
                if (window.__downloadFile) window.__downloadFile(meta.blob, 'passport-sheet-' + st.specId + '.pdf');
                var note = $('ppSheetNote');
                if (note) note.textContent = u('sheet_made', '')
                    .replace(':n', meta.count).replace(':cols', meta.cols).replace(':rows', meta.rows).replace(':pages', meta.pages);
                try {
                    window.__aeToolComplete && window.__aeToolComplete({
                        doc: st.specId, kind: 'sheet', sheet: $('ppSheet').value, pages: meta.pages,
                        count: meta.count, corner: st.corner.on ? st.corner.mm : 0
                    });
                } catch (_) { }
            } catch (e) {
                window.__toast && window.__toast(u('err_print', ''), 'error');
                try { window.__aeToolError && window.__aeToolError({ error_type: 'sheet_failed', msg: String((e && e.message) || e).slice(0, 140) }); } catch (_) { }
            }
            pr.disabled = false;
        });

        syncRangeLabels();
        syncBgUi();
        syncCornerUi();
        drawSpecSheet();
        showWorkspace(false);

        // The model is 3–4 MB and the first upload is seconds away; start it now
        // so the wait lands on reading the spec sheet rather than on the photo.
        E.warm();
        if (window.FRLandmarks && window.FRLandmarks.onProgress) {
            window.FRLandmarks.onProgress(function (p) {
                if (!p || $('ppBusy').hidden) return;
                if (p.phase === 'model' && p.total > 0) {
                    busy(u('loading_model', ''), (p.got / 1048576).toFixed(1) + ' / ' + (p.total / 1048576).toFixed(1) + ' ' + u('mb', 'MB'), p.got / p.total * 100);
                }
            });
        }

        exposeHooks();
    }

    function bindRange(id, outId, apply, fmt, after) {
        var el = $(id); if (!el) return;
        el.addEventListener('input', function () {
            apply(+el.value);
            var o = $(outId); if (o) o.textContent = fmt(+el.value);
            if (st.src) { if (after) after(); else render(); }
        });
    }
    function syncRangeLabels() {
        var pairs = [['ppHair', 'ppHairV', '%'], ['ppZoom', 'ppZoomV', '%'], ['ppRotate', 'ppRotateV', '°']];
        pairs.forEach(function (p) {
            var el = $(p[0]), o = $(p[1]);
            if (el && o) o.textContent = el.value + p[2];
        });
        var v = $('ppCornerMmV'); if (v) v.textContent = fmtMm(st.corner.mm);
    }

    // ── programmatic hooks (Playwright + inter-tool) ─────────────────────────
    function exposeHooks() {
        window.__ppState = function () {
            return st.tr ? {
                specId: st.specId, scale: st.tr.s, crownY: st._crownY, chinY: st._g.chin.y, k: st.k,
                manualCrownY: st.manualCrownY, TW: st.tr.TW, TH: st.tr.TH, dispScale: st.dispScale,
                srcW: st.src && st.src.width, srcH: st.src && st.src.height,
                corner: JSON.parse(JSON.stringify(st.corner)), gray: st.gray, bg: st.bgMode
            } : null;
        };
        window.__ppPickDoc = function (id) { pickDoc(id); return st.specId; };
        window.__ppSetCorner = function (o) {
            Object.assign(st.corner, o || {});
            syncCornerUi(); drawSpecSheet();
            if (st.src) finish(SPECS[st.specId]);
            return JSON.parse(JSON.stringify(st.corner));
        };
        window.__ppSetGray = function (on) {
            st.gray = !!on; var g = $('ppGray'); if (g) g.checked = st.gray;
            if (st.src) finish(SPECS[st.specId]);
            return st.gray;
        };
        window.__ppGetMeasured = async function () {
            if (!st._rendered) return { ok: false };
            var v = await E.analyze(st._rendered, { deep: false }), rc = st._rendered;
            if (!v.ok) return { ok: false, faces: v.faces };
            var exp = await E.exportJpegSizeBand(rc, SPECS[st.specId]);
            var spec = SPECS[st.specId];
            return {
                ok: true, specId: st.specId, target: { w: rc.width, h: rc.height },
                measured: {
                    head_pct_of_frame: +((v.geom.chin.y - v.crownY) / rc.height * 100).toFixed(1),
                    head_mm: +((v.geom.chin.y - v.crownY) / rc.height * spec.print_h_mm).toFixed(1),
                    top_margin_mm: +(v.crownY / rc.height * spec.print_h_mm).toFixed(1),
                    eye_y_from_top_pct: +(v.geom.eyeY / rc.height * 100).toFixed(1),
                    roll_deg: +v.geom.rollDeg.toFixed(2), crown_y: +v.crownY.toFixed(0),
                    eye_x_offset_from_center: +(v.geom.eyeX - rc.width / 2).toFixed(1)
                },
                export: { sizeKB: exp.sizeKB, quality: exp.quality, warned: exp.warned }
            };
        };
        window.__ppProcessUrl = async function (url, specId) {
            var r = await fetch(url), blob = await r.blob();
            if (specId) pickDoc(specId, { silent: true });
            var okk = await loadFile(blob);
            return okk ? await window.__ppGetMeasured() : { ok: false };
        };
        window.__ppSetBg = async function (mode) {
            st.bgMode = mode; syncBgUi();
            if (mode === 'none') { st._bgApplied = st._base; finish(SPECS[st.specId]); } else await applyHeavy();
        };
        /** Average colour of a small patch, in output pixels — used by tests to
         *  prove the corner is white and the backdrop is what the spec asks. */
        window.__ppSamplePx = function (fx, fy, size) {
            if (!st._rendered) return null;
            var W = st._rendered.width, H = st._rendered.height, s = size || 6;
            var x = Math.max(0, Math.min(W - s, Math.round(fx * W - s / 2)));
            var y = Math.max(0, Math.min(H - s, Math.round(fy * H - s / 2)));
            var c = st._rendered.getContext('2d').getImageData(x, y, s, s).data, r = 0, g = 0, b = 0, n = c.length / 4;
            for (var i = 0; i < c.length; i += 4) { r += c[i]; g += c[i + 1]; b += c[i + 2]; }
            return { r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n) };
        };
        window.__ppSampleCorner = function () { return window.__ppSamplePx(0.02, 0.02, 6); };
        window.__ppBgAvailable = function (specId) { return SPECS[specId || st.specId].allow_ai_retouch !== false; };
        window.__ppExportInfo = async function () {
            if (!st._rendered) return null;
            var spec = SPECS[st.specId], exp = await E.exportJpegSizeBand(st._rendered, spec);
            return { sizeKB: exp.sizeKB, type: exp.blob.type, w: exp.width, h: exp.height };
        };
        window.__ppBuildSheet = async function (sheet, count, lines) {
            if (!st._rendered || !window.PassportPrintSheet) return null;
            var spec = SPECS[st.specId], exp = await E.exportJpegSizeBand(st._rendered, spec);
            var meta = await window.PassportPrintSheet.build(exp.blob, spec, { sheet: sheet, count: count, lines: lines });
            return { pages: meta.pages, cols: meta.cols, rows: meta.rows, perPage: meta.perPage,
                     sizeKB: +(meta.blob.size / 1024).toFixed(1), pdfType: meta.blob.type,
                     pageWmm: meta.pageWmm, pageHmm: meta.pageHmm, cellWmm: meta.cellWmm, cellHmm: meta.cellHmm };
        };
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
    else wire();
})();
